import React, { useState, useEffect } from "react";
import {
  FileSpreadsheet,
  Printer,
  Search,
  Loader2,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import api from "../api/axiosInstance";

const AttendanceRegister = () => {
  const [employees, setEmployees] = useState([]);
  const [selectedMonth, setSelectedMonth] = useState(
    new Date().toISOString().slice(0, 7),
  );
  const [registerData, setRegisterData] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    loadRegister();
  }, [selectedMonth]);

  const loadRegister = async () => {
    setLoading(true);
    try {
      // This API will return all attendance for all employees for the month
      const res = await api.get(
        `get_attendance_register.php?month=${selectedMonth}`,
      );
      setEmployees(res.data.employees || []);
      setRegisterData(res.data.attendance || {});
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const getDaysInMonth = () => {
    const [year, month] = selectedMonth.split("-").map(Number);
    return new Date(year, month, 0).getDate();
  };

  const daysCount = getDaysInMonth();
  const daysArray = Array.from({ length: daysCount }, (_, i) => i + 1);

  const filteredEmployees = employees.filter((e) =>
    e.name.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  const getStatusStyle = (status) => {
    switch (status) {
      case "Present":
        return "bg-emerald-500 text-white";
      case "Absent":
        return "bg-rose-500 text-white";
      case "Half-day":
        return "bg-amber-500 text-white";
      default:
        return "bg-slate-50 text-slate-300";
    }
  };

  return (
    <div className="space-y-6 p-4">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
        <div>
          <h1 className="text-3xl font-black text-slate-900 italic uppercase tracking-tighter">
            Workforce Register
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest">
            Monthly Attendance Master Sheet
          </p>
        </div>

        <div className="flex flex-wrap gap-3">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
              size={14}
            />
            <input
              type="text"
              placeholder="Search staff..."
              className="pl-10 pr-4 py-2 bg-white border border-slate-200 rounded-xl font-bold text-xs outline-none focus:border-indigo-500"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <input
            type="month"
            className="p-2 bg-white border border-slate-200 rounded-xl font-black text-xs outline-none"
            value={selectedMonth}
            onChange={(e) => setSelectedMonth(e.target.value)}
          />
          <button className="bg-emerald-600 text-white px-4 py-2 rounded-xl font-black text-[10px] uppercase shadow-md flex items-center gap-2 hover:bg-slate-900 transition-all">
            <FileSpreadsheet size={16} /> Export
          </button>
        </div>
      </div>

      {loading ? (
        <div className="h-96 flex items-center justify-center font-black text-slate-300 animate-pulse uppercase italic">
          Generating Master Sheet...
        </div>
      ) : (
        <div className="bg-white rounded-[32px] border border-slate-100 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-slate-900 text-white">
                  <th className="sticky left-0 z-20 bg-slate-900 p-4 text-[10px] font-black uppercase text-left border-r border-slate-800 min-w-[200px]">
                    Employee Name
                  </th>
                  {daysArray.map((day) => {
                    const dateStr = `${selectedMonth}-${day.toString().padStart(2, "0")}`;
                    const isSunday = new Date(dateStr).getDay() === 0;
                    return (
                      <th
                        key={day}
                        className={`p-2 text-[9px] font-black text-center min-w-[40px] border-r border-slate-800 ${isSunday ? "text-rose-400 bg-slate-800" : ""}`}
                      >
                        {day}
                        <br />
                        <span className="opacity-50">
                          {new Date(dateStr).toLocaleDateString("en-US", {
                            weekday: "narrow",
                          })}
                        </span>
                      </th>
                    );
                  })}
                  <th className="p-4 text-[10px] font-black uppercase text-center bg-indigo-600">
                    Total
                  </th>
                </tr>
              </thead>
              <tbody>
                {filteredEmployees.map((emp) => {
                  let presentCount = 0;
                  return (
                    <tr
                      key={emp.id}
                      className="hover:bg-slate-50 transition-colors border-b border-slate-50"
                    >
                      <td className="sticky left-0 z-10 bg-white p-4 border-r border-slate-100 group-hover:bg-slate-50">
                        <p className="font-black text-slate-900 text-xs uppercase italic truncate">
                          {emp.name}
                        </p>
                        <p className="text-[8px] font-bold text-slate-400 uppercase leading-none">
                          {emp.pay_type}
                        </p>
                      </td>
                      {daysArray.map((day) => {
                        const dateStr = `${selectedMonth}-${day.toString().padStart(2, "0")}`;
                        const record = registerData[emp.id]?.[dateStr];
                        if (
                          record?.status === "Present" ||
                          record?.status === "Half-day"
                        ) {
                          presentCount += parseFloat(record.multiplier || 0);
                        }

                        return (
                          <td
                            key={day}
                            className="p-1 border-r border-slate-50"
                          >
                            <div
                              className={`w-8 h-8 mx-auto flex items-center justify-center rounded-lg font-black text-[9px] ${getStatusStyle(record?.status)}`}
                            >
                              {record
                                ? record.status === "Half-day"
                                  ? "HD"
                                  : record.status[0]
                                : "-"}
                            </div>
                          </td>
                        );
                      })}
                      <td className="p-4 text-center font-black text-indigo-600 bg-indigo-50/50 text-xs">
                        {presentCount}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AttendanceRegister;
